import React, { useMemo, useState, useEffect } from 'react';
import type { TrainWrapperAnimation } from './constants';

interface TrainWrapperProps {
  children: React.ReactNode;
  /** Which animation to use for this train instance */
  animationType?: TrainWrapperAnimation;
  className?: string;
}

const ANIMATION_DURATION_MS = 400;

/**
 * Wrapper that applies a small visual effect (blink, zoom, color, etc.) to the train.
 */
export const TrainWrapper: React.FC<TrainWrapperProps> = ({
  children,
  animationType = 'blink',
  className,
}) => {
  const [key, setKey] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setKey((k) => k + 1), ANIMATION_DURATION_MS);
    return () => clearInterval(id);
  }, []);

  const style = useMemo(() => {
    const base: React.CSSProperties = {
      display: 'inline-flex',
      flexWrap: 'nowrap',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.25rem 0.5rem',
      borderRadius: '12px',
      border: '2px solid rgba(52, 211, 153, 0.5)',
      backgroundColor: 'rgba(52, 211, 153, 0.15)',
      transition: `transform ${ANIMATION_DURATION_MS}ms ease, opacity ${ANIMATION_DURATION_MS}ms ease, filter ${ANIMATION_DURATION_MS}ms ease`,
      whiteSpace: 'nowrap',
    };
    switch (animationType) {
      case 'blink':
        return { ...base, opacity: key % 2 === 0 ? 1 : 0.85 };
      case 'zoomInOut':
        return {
          ...base,
          transform: key % 2 === 0 ? 'scale(1.1)' : 'scale(0.9)',
        };
      case 'colorShift':
        return {
          ...base,
          filter: key % 2 === 0 ? 'hue-rotate(0deg)' : 'hue-rotate(15deg)',
        };
      case 'pulse':
        return {
          ...base,
          transform: key % 2 === 0 ? 'scale(1.05)' : 'scale(1)',
        };
      case 'shake':
        return {
          ...base,
          transform: key % 2 === 0 ? 'translateX(-2px)' : 'translateX(2px)',
        };
      default:
        return base;
    }
  }, [animationType, key]);

  return (
    <div className={className} style={style}>
      {children}
    </div>
  );
};
